import React, { Component } from "react";
import { ErrorWrapper } from "../components/Error/Error";
import { Modal } from "../components/Modal/ModalPopup";
import { isFF } from "../utils/feature-flags";
import { IMPROVE_GLOBAL_ERROR_MESSAGES } from "../providers/ApiProvider";
import { useTranslation } from "../i18n/LocaleProvider";
import { healIfStale } from "../utils/build-check";

export const ErrorContext = React.createContext();

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(error, { componentStack }) {
    // Stale-deploy chunk errors ("Loading (CSS) chunk N failed") land
    // HERE, not on window.onerror — React lazy() rejections are handled
    // by the boundary, so the window-level self-heal in main.tsx never
    // fires. Heal in place: one automatic reload fetches the fresh
    // runtime + chunk names (entry assets are served no-cache). The
    // sessionStorage guard stops a reload loop if the server is
    // genuinely missing files.
    if (/Loading (CSS )?chunk [\w-]+ failed/i.test(error?.message || "")) {
      const GUARD = "chunk-error-reloaded";
      if (!sessionStorage.getItem(GUARD)) {
        sessionStorage.setItem(GUARD, "1");
        location.reload();
        return;
      }
    }

    // Any other failure of an app older than the server's build: load the
    // new build instead (utils/build-check.ts). The error shows meanwhile.
    healIfStale();

    // Log to the browser console for local/dev visibility. The
    // upstream build forwarded these to Sentry too; AfriAnnotate
    // doesn't wire a telemetry backend yet, so the console is the
    // single source of truth for runtime errors during development
    // and ``hasError`` below drives the user-facing modal.
    console.error(error);
    if (componentStack) {
      console.error("Component stack:\n", componentStack);
    }
    this.setState({
      error,
      hasError: true,
      errorInfo: componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      const { error, errorInfo } = this.state;

      const goBack = () => {
        // usually this will trigger React Router in the broken app, which is not helpful
        history.back();
        // so we reload app totally on that previous page after some delay for Router's stuff
        setTimeout(() => location.reload(), 32);
      };

      // We will capture the stacktrace in Sentry, so we don't need to show it in the modal
      // It is not actionable to the user, let's not show it
      const stacktrace = isFF(IMPROVE_GLOBAL_ERROR_MESSAGES)
        ? undefined
        : `${errorInfo ? `Component Stack: ${errorInfo}` : ""}\n\n${this.state.error?.stack ?? ""}`;

      return (
        <Modal onHide={() => location.reload()} style={{ width: "60vw" }} visible bare>
          <div style={{ padding: 40 }}>
            <ErrorWrapper
              title="Runtime error"
              message={error}
              stacktrace={stacktrace}
              onGoBack={goBack}
              onReload={() => location.reload()}
            />
          </div>
        </Modal>
      );
    }

    return (
      <ErrorContext.Provider
        value={{
          hasError: this.state.hasError,
          error: this.state.error,
          errorInfo: this.state.errorInfo,
          silence: this.silence,
          unsilence: this.unsilence,
        }}
      >
        {this.props.children}
      </ErrorContext.Provider>
    );
  }
}

export const ErrorUI = () => {
  const { t } = useTranslation();
  const context = React.useContext(ErrorContext);

  return context.hasError && <div className="error">{t("app.errorboundary.error_occurred")}</div>;
};
